import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Nav from "@/components/Nav";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "pending", payment_status: "pending" });
  const { refresh } = useAuth();
  const stopRef = useRef(false);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) return;
    let timer;
    const tick = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setState(data);
        if (data.payment_status === "paid" || data.payment_status === "failed" || data.payment_status === "expired") {
          if (data.payment_status === "paid") refresh();
          stopRef.current = true;
          return;
        }
      } catch { /* ignore */ }
      attemptsRef.current += 1;
      if (!stopRef.current && attemptsRef.current < 40) {
        timer = setTimeout(tick, 2000);
      }
    };
    tick();
    return () => { stopRef.current = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const paid = state.payment_status === "paid";
  const failed = state.payment_status === "failed" || state.payment_status === "expired";

  return (
    <div className="min-h-screen">
      <Nav />
      <main className="max-w-xl mx-auto px-6 py-24 text-center">
        {paid ? (
          <>
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-8 h-8 text-emerald-400" />
            </div>
            <h1 className="mt-6 text-3xl font-medium tracking-tight">Payment successful</h1>
            <p className="mt-3 text-muted-foreground">
            {state.plan_name ? `Welcome to ${state.plan_name} — ${state.monthly_leads || 0} leads per month are now active on your account.` : "Your subscription is now active."}
            </p>
            <div className="mt-8 flex gap-3 justify-center">
              <Button asChild className="rounded-full bg-primary hover:bg-[#FF7043] text-white" data-testid="success-go-dashboard">
                <Link to="/dashboard">Go to dashboard</Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full">
                <Link to="/new">Create a lead list</Link>
              </Button>
            </div>
          </>
        ) : failed ? (
          <>
            <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center mx-auto">
              <XCircle className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="mt-6 text-3xl font-medium tracking-tight">Payment not completed</h1>
            <p className="mt-3 text-muted-foreground">Your card wasn't charged. You can try again.</p>
            <div className="mt-8">
              <Button asChild className="rounded-full bg-primary hover:bg-[#FF7043] text-white">
                <Link to="/pricing">Back to pricing</Link>
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="w-16 h-16 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center mx-auto">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
            <h1 className="mt-6 text-3xl font-medium tracking-tight">Confirming payment…</h1>
            <p className="mt-3 text-muted-foreground">This usually takes a few seconds.</p>
          </>
        )}
      </main>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen">
      <Nav />
      <main className="max-w-xl mx-auto px-6 py-24 text-center">
        <h1 className="text-3xl font-medium tracking-tight">Checkout cancelled</h1>
        <p className="mt-3 text-muted-foreground">No charge was made.</p>
        <div className="mt-8">
          <Button asChild className="rounded-full bg-primary hover:bg-[#FF7043] text-white">
            <Link to="/pricing">Back to pricing</Link>
          </Button>
        </div>
      </main>
    </div>
  );
}
